import { useMemo } from 'react'
import type { CoupleFeedback, FeedbackResponse } from '@/utils/api'

interface FeedbackQuestion {
  id: number
  question: string
  answer?: string | null
}

interface FeedbackTabProps {
  data?: CoupleFeedback | null
}

const mapResponses = (responses?: FeedbackResponse[] | null): FeedbackQuestion[] =>
  (responses ?? []).map((response, index) => ({
    id: index + 1,
    question: response.question || `Question ${index + 1}`,
    answer: response.answer ?? '',
  }))

// Radio button component
const RadioButton = ({ selected }: { selected: boolean }) => (
  <div className={`w-[24px] h-[24px] rounded-full border-2 flex items-center justify-center ${
    selected ? 'border-[#FF6600]' : 'border-[#4e4d4d]'
  }`}>
    {selected && (
      <div className='w-[8px] h-[8px] rounded-full bg-[#FF6600]' />
    )}
  </div>
)

const QuestionCard = ({ question }: { question: FeedbackQuestion }) => {
  const normalized = (question.answer || '').trim().toLowerCase()
  const isYes = normalized === 'yes'
  const isNo = normalized === 'no'

  return (
    <div className='flex flex-col gap-[29px]'>
      <div className='flex flex-col gap-4'>
        <p className='text-[16px] text-[#4e4d4d] font-normal leading-6'>
          Question {question.id}
        </p>
        <p className='text-[16px] text-[#151515] font-medium leading-6'>
          {question.question}
        </p>
      </div>

      {(isYes || isNo) ? (
        <div className='flex flex-col gap-3'>
          {/* Yes Option */}
          <div
            className={`flex items-center justify-between px-6 py-2 rounded-r-[6px] ${
              isYes
                ? 'bg-white border-l-[6px] border-[#FF6600] shadow-[2px_10px_53px_-10px_rgba(16,24,40,0.09)]'
                : 'border border-[#e0e0e0] rounded-[6px]'
            }`}
          >
            <span className={`text-[16px] font-medium ${isYes ? 'text-[#151515]' : 'text-[#4e4d4d]'}`}>
              Yes
            </span>
            <RadioButton selected={isYes} />
          </div>

          {/* No Option */}
          <div
            className={`flex items-center justify-between px-6 py-2 rounded-r-[6px] ${
              isNo
                ? 'bg-white border-l-[6px] border-[#FF6600] shadow-[2px_10px_53px_-10px_rgba(16,24,40,0.09)]'
                : 'border border-[#e0e0e0] rounded-[6px]'
            }`}
          >
            <span className={`text-[16px] font-medium ${isNo ? 'text-[#151515]' : 'text-[#4e4d4d]'}`}>
              No
            </span>
            <RadioButton selected={isNo} />
          </div>
        </div>
      ) : (
        <div className='rounded-[6px] border border-[#e0e0e0] px-6 py-3 text-[16px] text-[#4e4d4d]'>
          <span className='font-semibold'>Answer:</span> {question.answer || 'No response'}
        </div>
      )}
    </div>
  )
}

export default function FeedbackTab({ data }: FeedbackTabProps) {
  const manQuestions = useMemo(
    () => mapResponses(data?.personOne?.responses),
    [data?.personOne?.responses]
  )
  const womanQuestions = useMemo(
    () => mapResponses(data?.personTwo?.responses),
    [data?.personTwo?.responses]
  )

  return (
    <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
      {/* Man Section */}
      <div className='flex flex-col gap-4'>
        <div className='flex items-center justify-between'>
          <h2 className='text-[18px] font-bold text-[#151515] leading-[28px]'>Man</h2>
        </div>
        <div className='bg-white border border-[#e8e8e8] rounded-[10px] p-8'>
          <div className='flex flex-col gap-8'>
            {manQuestions.length === 0 && (
              <p className='text-[14px] text-[#4e4d4d]'>No feedback submitted yet.</p>
            )}
            {manQuestions.map((question) => (
              <QuestionCard key={question.id} question={question} />
            ))}
          </div>
        </div>
      </div>

      {/* Woman Section */}
      <div className='flex flex-col gap-4'>
        <div className='flex items-center justify-between'>
          <h2 className='text-[18px] font-bold text-[#151515] leading-[28px]'>Woman</h2>
        </div>
        <div className='bg-white border border-[#e8e8e8] rounded-[10px] p-8'>
          <div className='flex flex-col gap-8'>
            {womanQuestions.length === 0 && (
              <p className='text-[14px] text-[#4e4d4d]'>No feedback submitted yet.</p>
            )}
            {womanQuestions.map((question) => (
              <QuestionCard key={question.id} question={question} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
